import { createSelector } from 'reselect';
import { Selector } from 'react-redux';

import {
  ActionType,
  ErrorTypes,
  RoleTypes,
  ICustomMenuItem,
  StoreOptionsType,
  OptionType,
} from 'interfaces';
import { FAILURE, SUCCESS } from 'services/api';
import {
  APP_CONFIG,
  APP_OPTIONS,
  PRODUCT_CURRENCY,
  CHECK_AUTH,
  FEATURE_BY_NAME,
  FORGOT_PASSWORD,
  LOGIN,
  LOGOUT,
  GET_CUSTOM_MENU_ITEMS_ACTION,
  SET_APP_LOADING,
  SET_AUTH_LOADING,
  SET_LOGIN_LOADING,
  SET_FEATURE_LOADING,
  SET_MENU_LOADING,
  SET_OPTIONS_LOADING,
  SET_POINTS_LOADING,
  SET_CHECK_PASSWORD_RESET_TOKEN_LOADING,
  SIGNUP,
  UPDATE_USER_CONDITIONS_AND_TERMS,
  USER_POINTS,
  CHECK_SSO_AZURE_CONFIG,
  CHECK_SSO_JWT_CONFIG,
  CHECK_SSO_SAML_CONFIG,
  CHECK_PASSWORD_RESET_TOKEN,
} from 'actions/app.actions';

type FeatureType = {
  data?: {
    enabled: boolean;
    attrs: any;
  };
  error?: ErrorTypes | null;
  isLoading: boolean;
};

type passwordResetTokenType = {
  isLoading: boolean;
  isValid?: boolean;
  email?: string;
  error?: ErrorTypes | null;
};

export interface IAppState {
  loading: boolean;
  auth: {
    token: string;
    userInfos: {
      isCGUAccepted?: boolean;
      id?: string;
      username?: string;
      userRoles?: RoleTypes[];
      userGroups?: string[];
      points?: number;
      pointsLoading: boolean;
      profile?: {
        firstName: string;
        lastName: string;
      };
    };
    hasSsoAzureConfigured: boolean;
    hasSsoJwtConfigured: boolean;
    hasSsoSamlConfigured: boolean;
    loading: boolean;
    loginLoading: boolean;
    error: ErrorTypes | null;
    passwordResetToken: passwordResetTokenType;
  };
  config: { [key: string]: any };
  configError: ErrorTypes | null;
  productCurrency: string | null;
  productCurrencyError: ErrorTypes | null;
  menu: {
    loading: boolean;
    customMenuItems: ICustomMenuItem[];
  };
  options: StoreOptionsType;
  features: {
    [featureName: string]: FeatureType;
  };
}

export const initialState: IAppState = {
  loading: true,
  auth: {
    passwordResetToken: {
      isLoading: false,
      error: null,
    },
    token: '',
    userInfos: {
      pointsLoading: false,
    },
    hasSsoAzureConfigured: false,
    hasSsoJwtConfigured: false,
    hasSsoSamlConfigured: false,
    loading: false, // true when checking auth (/me)
    loginLoading: false, // true when logging (/login_check or forgot password, create acc etc.)
    error: null,
  },
  config: {},
  configError: null,
  productCurrency: null,
  productCurrencyError: null,
  menu: {
    loading: false,
    customMenuItems: [],
  },
  options: {
    list: [],
    isLoading: false,
    error: null,
  },
  features: {},
};

export const appReducerSelector = (state: { app: IAppState }): IAppState =>
  state.app;

export const tokenSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.token,
);

export const authLoadingSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.loading || false,
);

export const ssoAzureSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.hasSsoAzureConfigured,
);

export const ssoJwtSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.hasSsoJwtConfigured,
);

export const ssoSamlSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.hasSsoSamlConfigured,
);

export const passwordResetTokenSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.passwordResetToken,
);

export const loginLoadingSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.loginLoading || false,
);

export const userInfosSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.userInfos || { pointsLoading: false },
);

export const appConfigSelector = createSelector(
  appReducerSelector,
  (app) => app.config || {},
);

export const appOptionsSelector = createSelector(
  appReducerSelector,
  (app) => app.options || {},
);

export const currencySelector = createSelector(
  appOptionsSelector,
  (options: StoreOptionsType): string =>
    options.list.find((option: OptionType) => option.name === 'CURRENCY_NAME')
      ?.value ?? '',
);

export const clientIDLabelSelector = createSelector(
  appOptionsSelector,
  (options: StoreOptionsType): string =>
    options.list.find((option: OptionType) => option.name === 'clientID_label')
      ?.value ?? '',
);

export interface IColorMap {
  primary?: string;
  secondary?: string;
  tertiary?: string;
  accent?: string;
  background?: string;
}

export const colorsSelector = createSelector(
  appOptionsSelector,
  (options: StoreOptionsType): IColorMap =>
    options.list.reduce((acc: IColorMap, option: OptionType) => {
      const value = option.value;

      switch (option.name) {
        case '$primaryHue':
          return { ...acc, primary: value };
        case '$secondaryHue':
          return { ...acc, secondary: value };
        case '$thirdHue':
          return { ...acc, tertiary: value };
        case '$accentHue':
          return { ...acc, accent: value };
        case '$backgroundHue':
          return { ...acc, background: value };
        default:
          return acc;
      }
    }, {}),
);

export const menuSelector = createSelector(
  appReducerSelector,
  (app) => app.menu || {},
);

export const featureSelector = (
  featureName: string,
): Selector<{ app: IAppState }, FeatureType> =>
  createSelector(appReducerSelector, (app) => app.features[featureName]);

export const productCurrencySelector = createSelector(
  appReducerSelector,
  (app) => app.productCurrency,
);

const patchPasswordResetToken = (
  state: IAppState,
  props: Partial<passwordResetTokenType>,
): IAppState => ({
  ...state,
  auth: {
    ...state.auth,
    passwordResetToken: {
      ...state.auth.passwordResetToken,
      ...props,
    },
  },
});

export const appReducer = (
  state: IAppState = initialState,
  action: ActionType,
): IAppState => {
  const { payload } = action;

  switch (action.type) {
    case LOGOUT:
      return {
        ...state,
        loading: false,
        auth: {
          ...state.auth,
          token: '',
          userInfos: { pointsLoading: false },
          loading: false,
          error: null,
        },
      };
    case SET_APP_LOADING:
      return {
        ...state,
        loading: payload.isLoading,
      };
    case SET_AUTH_LOADING:
      return {
        ...state,
        auth: {
          ...state.auth,
          loading: payload.isLoading,
        },
      };
    case SET_LOGIN_LOADING:
      return {
        ...state,
        auth: {
          ...state.auth,
          loginLoading: payload.isLoading,
        },
      };
    case CHECK_AUTH[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          token: payload.token,
          userInfos: payload.userInfos,
        },
      };
    case CHECK_SSO_AZURE_CONFIG[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          hasSsoAzureConfigured: payload.hasSsoAzureConfigured,
        },
      };
    case CHECK_SSO_JWT_CONFIG[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          hasSsoJwtConfigured: payload.hasSsoJwtConfigured,
        },
      };
    case CHECK_SSO_SAML_CONFIG[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          hasSsoSamlConfigured: payload.hasSsoSamlConfigured,
        },
      };
    case CHECK_PASSWORD_RESET_TOKEN[SUCCESS]:
      return patchPasswordResetToken(state, {
        isValid: payload.isValid,
        email: payload.email,
        error: null,
      });
    case CHECK_PASSWORD_RESET_TOKEN[FAILURE]:
      return patchPasswordResetToken(state, {
        error: payload.error,
        isValid: undefined,
        email: undefined,
      });
    case SET_CHECK_PASSWORD_RESET_TOKEN_LOADING:
      return patchPasswordResetToken(state, {
        isLoading: payload.isLoading,
      });
    case SIGNUP[FAILURE]:
      return {
        ...state,
        auth: {
          ...state.auth,
          error: payload.error,
        },
      };
    case LOGIN[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          token: payload.token,
        },
      };
    case FORGOT_PASSWORD[SUCCESS]:
    case SIGNUP[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          error: null,
        },
      };
    case APP_CONFIG[SUCCESS]:
      return {
        ...state,
        config: {
          ...payload.data,
          programUrl: payload.data.programUrl.match(/^http(|s):\/\//)
            ? payload.data.programUrl
            : `${window.location.protocol}//${payload.data.programUrl}`,
        },
        configError: null,
      };
    case APP_CONFIG[FAILURE]:
      return {
        ...state,
        config: {},
        configError: payload,
      };
    case PRODUCT_CURRENCY[SUCCESS]:
      return {
        ...state,
        productCurrency: payload.data,
        productCurrencyError: null,
      };
    case PRODUCT_CURRENCY[FAILURE]:
      return {
        ...state,
        productCurrency: null,
        productCurrencyError: payload,
      };
    case SET_OPTIONS_LOADING: {
      return {
        ...state,
        options: {
          ...state.options,
          isLoading: payload.isLoading,
        },
      };
    }
    case APP_OPTIONS[SUCCESS]: {
      return {
        ...state,
        options: {
          ...state.options,
          list: payload.data,
          error: null,
        },
      };
    }
    case APP_OPTIONS[FAILURE]: {
      return {
        ...state,
        options: {
          ...state.options,
          list: [],
          error: payload,
        },
      };
    }
    case SET_MENU_LOADING:
      return {
        ...state,
        menu: {
          ...state.menu,
          loading: payload.isLoading,
        },
      };
    case GET_CUSTOM_MENU_ITEMS_ACTION[SUCCESS]: {
      const { menuItems } = payload;
      return {
        ...state,
        menu: {
          ...state.menu,
          customMenuItems: menuItems.sort(
            (a: ICustomMenuItem, b: ICustomMenuItem) => a.position - b.position,
          ),
        },
      };
    }
    case USER_POINTS[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          userInfos: {
            ...state.auth.userInfos,
            points: payload.points,
          },
        },
      };
    case UPDATE_USER_CONDITIONS_AND_TERMS[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          userInfos: {
            ...state.auth.userInfos,
            isCGUAccepted: payload.user.isCGUAccepted,
          },
        },
      };
    case SET_POINTS_LOADING:
      return {
        ...state,
        auth: {
          ...state.auth,
          userInfos: {
            ...state.auth.userInfos,
            pointsLoading: payload.isLoading,
          },
        },
      };
    case FEATURE_BY_NAME[SUCCESS]:
      return {
        ...state,
        features: {
          ...state.features,
          [payload.name]: {
            ...(state.features?.[payload.name] || {}),
            data: payload.data,
            error: null,
          },
        },
      };
    case FEATURE_BY_NAME[FAILURE]:
      return {
        ...state,
        features: {
          ...state.features,
          [payload.name]: {
            ...(state.features?.[payload.name] || {}),
            data: {},
            error: payload.error,
          },
        },
      };
    case SET_FEATURE_LOADING:
      return {
        ...state,
        features: {
          ...state.features,
          [payload.name]: {
            ...(state.features?.[payload.name] || {}),
            isLoading: payload.isLoading,
          },
        },
      };
    default:
      return state;
  }
};

export default appReducer;
